import {
	isChecklistIntroItem,
	isFullTestFormatIntroItem,
	isMicCheckIntroItem,
	isPartTestFormatIntroItem,
	isReadyToStartIntroItem,
} from "@features/session/helpers/intro-screens";
import { getResponseTimeLimit } from "@features/session/helpers/response-timing";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import useResponseControlsStore from "@features/session/hooks/useResponseControlsStore";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { handleNavigateBack } from "@helpers/common";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import {
	isRecordingPermissionDenied,
	requestRecordingPermission,
	subscribeRecordingPermissionGranted,
} from "@memorang/helpers/device";
import { getBrowserName } from "@memorang/helpers/platform";
import type { BaseItemSchema, PartTestFormatIntroItem } from "@memorang/types";
import useRecorder from "@memorang/ui/components/AudioRecorderContainer/useRecorder";
import { MicrophonePermissionDialog } from "@memorang/ui/components/MicroPhonePermissionDialog";
import BrowserMicCheck from "@memorang/ui/components/StudySession/SessionContent/pre-test/BrowserMicCheck";
import type { MicCheckState } from "@memorang/ui/components/StudySession/SessionContent/pre-test/MicCheckControls";
import PartIntro from "@memorang/ui/components/StudySession/SessionContent/pre-test/PartIntro";
import ReadyToStart from "@memorang/ui/components/StudySession/SessionContent/pre-test/ReadyToStart";
import TestChecklist from "@memorang/ui/components/StudySession/SessionContent/pre-test/TestChecklist";
import TestFormat from "@memorang/ui/components/StudySession/SessionContent/pre-test/TestFormat";
import usePlayer from "@memorang/ui/hooks/usePlayer";
import type { IconType } from "@memorang/ui/icon";
import { useEffect, useState } from "react";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
};

const PartTestFormatIntro = ({ item }: { item: PartTestFormatIntroItem }) => {
	const itemId = item.id!;
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const markResponseComplete = useResponseControlsStore(
		(s) => s.markResponseComplete,
	);
	const isResponseComplete = useResponseControlsStore((s) =>
		Boolean(s.completedItems[itemId]),
	);
	const timeLimit = getResponseTimeLimit(item.response);

	useEffect(() => {
		if (inReviewMode || isResponseComplete || typeof timeLimit !== "number") {
			return;
		}
		const timeout = setTimeout(
			() => markResponseComplete(itemId),
			timeLimit * 1000,
		);
		return () => clearTimeout(timeout);
	}, [
		itemId,
		timeLimit,
		inReviewMode,
		isResponseComplete,
		markResponseComplete,
	]);

	return (
		<PartIntro
			title={item.content.title || ""}
			tableTitle="Overview"
			sections={(item.content.sectionsTable ?? []).map((section) => ({
				section: `Section ${section.section}`,
				itemTypes: section.itemTypes.map((it) => it.name),
				timeAllowed: `${section.durationMinutes} minutes`,
			}))}
		/>
	);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
const IntroScreenContainer = ({ item }: Props) => {
	const [micState, setMicState] = useState<MicCheckState>("idle");
	const [recordedUri, setRecordedUri] = useState<string | null>(null);
	const [showPermissionDialog, setShowPermissionDialog] = useState(false);
	const [micBlocked, setMicBlocked] = useState(false);
	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);
	const { startRecording, stopRecording } = useRecorder({
		onRecordingComplete: setRecordedUri,
		captureError: (_context, error) => captureErrorToSentry(error),
	});
	const { playSound, pauseSound, isPlaying } = usePlayer(recordedUri);

	useEffect(() => {
		if (!showPermissionDialog) {
			return;
		}
		return subscribeRecordingPermissionGranted(() => {
			setMicBlocked(false);
			setShowPermissionDialog(false);
		});
	}, [showPermissionDialog]);

	if (isPartTestFormatIntroItem(item)) {
		return <PartTestFormatIntro item={item} />;
	}

	if (isChecklistIntroItem(item)) {
		return (
			<TestChecklist
				items={(item.content.checklistItems ?? []).map((ci) => ({
					icon: ci.icon as IconType,
					text: ci.text,
				}))}
			/>
		);
	}

	if (isMicCheckIntroItem(item)) {
		const handleStop = async () => {
			await stopRecording();
			setMicState("success");
			setUserResponse(item.id ?? 0, "mic_success");
		};

		const handlePlayback = () => {
			if (isPlaying) {
				pauseSound();
			} else {
				playSound();
			}
		};

		const startMicRecording = () => {
			setRecordedUri(null);
			startRecording(0);
			setMicState("recording");
		};

		const handleRecord = async () => {
			const granted = await requestRecordingPermission();
			if (!granted) {
				setMicBlocked(await isRecordingPermissionDenied());
				setShowPermissionDialog(true);
				return;
			}
			startMicRecording();
		};

		const browserName = getBrowserName();

		return (
			<>
				<BrowserMicCheck
					browserCheck={
						browserName
							? {
									title: item.content.browserCheck?.title || "Browser check",
									label: item.content.browserCheck?.label || "You are using:",
									browserName,
								}
							: undefined
					}
					microphoneCheck={{
						title: item.content.microphoneCheck?.title || "Microphone Check",
						intro: item.content.microphoneCheck?.intro || "",
						steps: (item.content.microphoneCheck?.steps ?? []).map(String),
						successMessage:
							item.content.microphoneCheck?.successMessage ||
							"Recording complete, microphone is working",
					}}
					micState={micState}
					isPlaying={isPlaying}
					onRecord={handleRecord}
					onStop={handleStop}
					onPlayback={recordedUri ? handlePlayback : undefined}
					onRecordAgain={handleRecord}
				/>
				<MicrophonePermissionDialog
					showDialog={showPermissionDialog}
					setShowDialog={() => {
						setShowPermissionDialog(false);
					}}
					variant="confirm-exit"
					blocked={micBlocked}
					onEnable={async () => {
						const granted = await requestRecordingPermission();
						if (!granted) {
							setMicBlocked(await isRecordingPermissionDenied());
							return;
						}
						setMicBlocked(false);
						setShowPermissionDialog(false);
						startMicRecording();
					}}
					onExit={() => {
						setShowPermissionDialog(false);
						handleNavigateBack("/(protected)/(tabs)/home");
					}}
				/>
			</>
		);
	}

	if (isFullTestFormatIntroItem(item)) {
		return (
			<TestFormat
				title={item.content.title || ""}
				intro={item.content.timerInfo || "Test Format"}
				parts={(item.content.partsOverview ?? []).map((part) => ({
					part: `${part.durationMinutes ? `Part ${part.part}` : part.part}`,
					content: part.name,
					timeAllowed: part.durationMinutes
						? `${part.durationMinutes} minutes`
						: "Not timed",
				}))}
				timerTitle="Timer"
				timerDescription={item.content.approximateDurationText || "2 hours"}
			/>
		);
	}

	if (isReadyToStartIntroItem(item)) {
		return (
			<ReadyToStart
				title={item.content.title || ""}
				callouts={(item.content.callouts ?? []).map((c) => ({
					icon: c.icon as IconType,
					text: c.text,
				}))}
			/>
		);
	}

	return null;
};

export default IntroScreenContainer;
